'use client';

import type { GamificationMissionGroups } from '@/components/features/gamification/missions/types';
import { cva } from 'class-variance-authority';
import { useState } from 'react';
import { GamificationMissionsContent } from '@/components/features/gamification/missions/components';
import { GamificationRewardsContent } from '@/components/features/gamification/rewards/components';
import { GmBundleOffers } from '@/components/features/gamification/shop/GmBundleOffers';
import { cn } from '@/utils/cn';
import { useGamificationTabs } from '../../../hooks/useGamificationTabs';
import { GamificationTabsList } from '../../GamificationTabsList';

const tabs = [
  { key: 'missions', value: 'missions' },
  { key: 'rewards', value: 'rewards' },
] as const;

const tabPanelVariants = cva('transition-all duration-300 ease-out', {
  variants: {
    active: {
      true: 'relative translate-y-0 opacity-100',
      false: 'pointer-events-none absolute inset-x-0 top-0 max-h-0 translate-y-2 overflow-hidden opacity-0',
    },
  },
});

interface GmMobileBarContentProps {
  loading: boolean;
  totalRewards?: number;
  missionGroups: GamificationMissionGroups;
  onExpired: VoidFunction;
}

type GmMobileBarTab = typeof tabs[number]['value'];

export const GmMobileBarContent = ({ loading, missionGroups, totalRewards, onExpired }: GmMobileBarContentProps) => {
  const [activeTab, setActiveTab] = useState<GmMobileBarTab>(tabs[0].value);
  const localizedTabs = useGamificationTabs(tabs, totalRewards);

  return (
    <>
      <GamificationTabsList
        tabs={localizedTabs}
        activeValue={activeTab}
        className="mb-3"
        variant="mobile"
        titleClassName="truncate text-sm"
        renderTab={({ tab, isActive, ...rest }) => (
          <button
            {...rest}
            role="tab"
            type="button"
            aria-selected={isActive}
            onClick={() => setActiveTab(tab.value)}
          />
        )}
      />

      <div className="relative">
        {/* Gamification Missions Tab */}
        <div
          aria-hidden={activeTab !== 'missions'}
          className={cn(
            tabPanelVariants({ active: activeTab === 'missions' }),
            'flex flex-col gap-4',
          )}
        >
          <GamificationMissionsContent
            loading={loading}
            onExpired={onExpired}
            missionGroups={missionGroups}
          />
        </div>

        {/* Gamification Rewards Tab */}
        <div
          aria-hidden={activeTab !== 'rewards'}
          className={cn(
            tabPanelVariants({ active: activeTab === 'rewards' }),
            'flex flex-col gap-4',
          )}
        >
          <GmBundleOffers />
          <GamificationRewardsContent
            className="gap-4 md:gap-4"
          />
        </div>
      </div>
    </>

  );
};
